/**
 * AuthModal — sign-in only (Feb 2026).
 *
 * Removed:
 *   - Signup mode (and the "Create Account" tab switcher). Self-signup is
 *     a dead end because `AuthContext.checkAccess` rejects any email not
 *     in `waitlist_requests` or `org_members`. We replaced it with an
 *     honest "Request access" link to /waitlist.
 *
 * Restored:
 *   - "Continue with Google" — the same invitation gate runs on every
 *     SIGNED_IN event, so Google OAuth is safe: an uninvited Google
 *     email gets signed out immediately with the invitation message.
 *
 * Remaining modes:
 *   - `login` (default) — email + password.
 *   - `reset` — request a password-reset email.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Eye, EyeOff, AlertCircle, Loader2, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

const translateAuthError = (error) => {
  const map = {
    'Invalid login credentials': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
    'Email not confirmed': 'يرجى تأكيد بريدك الإلكتروني أولاً',
    'Email rate limit exceeded': 'تم تجاوز الحد المسموح. يرجى المحاولة لاحقاً',
    'For security purposes, you can only request this once every 60 seconds': 'لأسباب أمنية، يمكنك طلب ذلك مرة كل 60 ثانية',
    'Auth session missing': 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى',
    'Token has expired or is invalid': 'الرابط منتهي الصلاحية أو غير صالح',
    'User not found': 'لم يتم العثور على حساب بهذا البريد الإلكتروني',
  };
  return map[error] || error || 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى';
};

const PasswordInput = ({ value, onChange, placeholder, showPassword, onToggle, testId }) => (
  <div className="relative">
    <input
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full p-3 border border-white/15 rounded-xl bg-white/5 text-[#EDEBE6] placeholder:text-[#77736B] pr-4 pl-12 focus:outline-none focus:ring-2 focus:ring-white/25 focus:border-white/40 transition-shadow"
      required
      data-testid={testId}
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B877F] hover:text-white transition-colors"
      tabIndex={-1}
      data-testid={`${testId}-toggle`}
    >
      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
    </button>
  </div>
);

export const AuthModal = () => {
  const {
    isAuthModalOpen,
    setIsAuthModalOpen,
    isAuthenticated,
    authError,
    setAuthError,
  } = useAuth();
  const { language } = useLanguage();
  // Two modes only — login (default) and reset. Signup removed.
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Password reset state
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const isArabic = language === 'ar';

  useEffect(() => {
    if (isAuthenticated && isAuthModalOpen) {
      setIsAuthModalOpen(false);
    }
  }, [isAuthenticated, isAuthModalOpen, setIsAuthModalOpen]);

  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setError('');
    setShowPassword(false);
    setResetEmailSent(false);
  }, []);

  const switchMode = (newMode) => {
    resetForm();
    if (authError) setAuthError('');
    setMode(newMode);
  };

  // Feb-07 2026 prod-bug fix: clearing the tab-lock flag when the
  // user explicitly attempts to sign in. Without this, an idle-locked
  // tab silently swallowed the post-signin redirect because the
  // SIGNED_IN handler in AuthContext skips the `/chat` bounce while
  // the flag is set (intent: don't yank passive rehydrations away
  // from the locked screen; collateral: also blocked explicit user
  // sign-ins through this modal). Mirrors LandingPage's `handleResume`
  // which clears the same flag.
  const unlockTab = useCallback(() => {
    try { sessionStorage.removeItem('bayenah_tab_locked'); } catch (_e) { /* ignore */ }
  }, []);

  const handleSignInWithGoogle = async () => {
    if (!supabase) return;
    setGoogleLoading(true);
    setError('');
    if (authError) setAuthError('');
    unlockTab();
    // Google users already have a verified identity — they don't need the
    // password-setup flow. Send them straight to /chat. ProtectedRoute
    // exempts Google users (`isGoogleUser` check) from the
    // needs_password_setup gate, and AuthContext.checkAccess still
    // verifies the email is on the invitation list before letting them in.
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/chat`,
        // Jul 2026 (open-signup launch): always show Google's account
        // chooser. Without this, Google silently reuses the browser's
        // active Google session — users who signed out of Bayenah were
        // logged straight back into the same account with no way to
        // pick a different one.
        queryParams: { prompt: 'select_account' },
      },
    });
    if (err) setError(translateAuthError(err.message));
    setGoogleLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    setLoading(true);
    setError('');
    if (authError) setAuthError('');
    unlockTab();
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) {
      setError(translateAuthError(err.message));
      return;
    }
    // DO NOT navigate here. The SIGNED_IN handler in AuthContext owns
    // routing for us: it runs `checkAccess`, and either
    //   • on access denied → keeps the modal open and sets `authError`
    //     so the "invitation only" banner stays visible, or
    //   • on access granted → navigates to /chat itself.
    // Hard-navigating from here previously beat checkAccess to the
    // browser-history push, wiping the React tree and losing the
    // gate message (audit May-21 2026, Test 5).
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    setLoading(true);
    setError('');
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (err) {
      setError(translateAuthError(err.message));
      return;
    }
    setResetEmail(email);
    setResetEmailSent(true);
  };

  // Texts
  const t = {
    login: isArabic ? 'تسجيل الدخول' : 'Sign In',
    reset: isArabic ? 'إعادة تعيين كلمة المرور' : 'Reset Password',
    email: isArabic ? 'البريد الإلكتروني' : 'Email',
    password: isArabic ? 'كلمة المرور' : 'Password',
    forgotPassword: isArabic ? 'نسيت كلمة المرور؟' : 'Forgot password?',
    google: isArabic ? 'المتابعة مع Google' : 'Continue with Google',
    or: isArabic ? 'أو' : 'or',
    sendReset: isArabic ? 'إرسال رابط التعيين' : 'Send Reset Link',
    backToLogin: isArabic ? 'العودة لتسجيل الدخول' : 'Back to login',
    loading: isArabic ? 'جاري التحميل...' : 'Loading...',
    noAccountPrompt: isArabic ? 'لا تملك حساباً؟' : 'No account?',
    requestAccess: isArabic ? 'اطلب الوصول' : 'Request access',
  };

  // C-2 (Feb 06 2026): bilingual invitation-gate / OAuth-error messages.
  // AuthContext now stores a structured `{ code, params }` payload
  // instead of a hardcoded English string (see AuthContext.jsx
  // L87-91 and L121-135). This helper translates the payload into
  // the user-visible string. Unknown shapes fall through to a
  // string render for backwards compatibility.
  const gateMessageFor = (raw) => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    const code = raw.code;
    const params = raw.params || {};
    if (code === 'invitation_required') {
      return isArabic
        ? 'الوصول إلى بيّنة بدعوة فقط. اطلب الوصول عبر bayenah.com'
        : 'Access to Bayenah is by invitation only. Request access at bayenah.com';
    }
    if (code === 'otp_expired') {
      return isArabic
        ? 'تم استخدام رابط الدعوة سابقاً أو انتهت صلاحيته. يرجى طلب دعوة جديدة من المسؤول.'
        : 'Your invite link has already been used or has expired. Please request a fresh invite from your admin.';
    }
    if (code === 'oauth_failed') {
      const errCode = params.errCode || 'unknown';
      const desc = params.desc || '';
      return isArabic
        ? `تعذّر معالجة رابط تسجيل الدخول (${errCode}). ${desc}`
        : `Sign-in link could not be processed (${errCode}). ${desc}`;
    }
    // Unknown structured code — surface the raw code for telemetry.
    return isArabic
      ? `حدث خطأ غير متوقع (${code}).`
      : `An unexpected error occurred (${code}).`;
  };

  // Reset email sent screen
  if (resetEmailSent) {
    return (
      <Dialog open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen}>
        <DialogContent className="max-w-md p-0 bg-[#101010] border border-white/10 rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.6)] overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-500 [&>button]:text-[#A8A49C] [&>button:hover]:text-white">
          <DialogTitle className="sr-only">
            {isArabic ? 'تحقق من بريدك الإلكتروني' : 'Check your email'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isArabic ? 'تم إرسال رابط إعادة تعيين كلمة المرور.' : 'Password reset link sent.'}
          </DialogDescription>
          <div className="text-center p-8" dir={isArabic ? 'rtl' : 'ltr'} data-testid="reset-email-sent-screen">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">تحقق من بريدك الإلكتروني</h3>
            <p className="text-[#6B6B6B] mb-2">تم إرسال رابط إعادة تعيين كلمة المرور إلى:</p>
            <p className="font-medium text-[#1A1A1A] mb-6">{resetEmail}</p>
            <button
              onClick={() => { setResetEmailSent(false); switchMode('login'); }}
              className="text-[#1a1a1a] font-medium hover:underline"
            >
              {t.backToLogin}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen}>
      <DialogContent className="max-w-md p-0 bg-[#101010] border border-white/10 rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.6)] overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-500 [&>button]:text-[#A8A49C] [&>button:hover]:text-white">
        <DialogTitle className="sr-only">
          {mode === 'login' ? (isArabic ? 'تسجيل الدخول إلى بيّنة' : 'Sign in to Bayenah') : (isArabic ? 'إعادة تعيين كلمة المرور' : 'Reset password')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {mode === 'login'
            ? (isArabic ? 'استخدم بريدك الإلكتروني وكلمة المرور لتسجيل الدخول.' : 'Use your email and password to sign in.')
            : (isArabic ? 'سنرسل رابط إعادة تعيين كلمة المرور إلى بريدك.' : 'We will email you a password reset link.')
          }
        </DialogDescription>
        {/* Bidi: surface direction must follow `isArabic`. The previous
            hardcoded `dir="rtl"` caused mixed-direction artifacts in
            English mode (e.g. "?Forgot password" — punctuation flipped
            to the wrong side). */}
        <div className="p-6" dir={isArabic ? 'rtl' : 'ltr'}>
          {/* Header — logo + title + subtitle (Jul 2026 redesign:
              Google-primary flow for the open-launch window) */}
          <div className="text-center mb-6">
            <div className="mb-4 flex justify-center">
              <img
                src="/brand/mark-white.png"
                alt="Bayenah"
                className="h-12 w-12 object-contain select-none"
                draggable="false"
              />
            </div>
            <h2
              className={cn('text-2xl font-semibold tracking-tight text-[#EDEBE6]', isArabic ? 'font-arabic' : 'font-serif')}
              data-testid="auth-modal-title"
            >
              {mode === 'login' ? t.login : t.reset}
            </h2>
            {mode === 'login' && (
              <p className={cn('mt-2 text-sm text-[#A8A49C]', isArabic && 'font-arabic')}>
                {isArabic
                  ? 'سجّل الدخول أو أنشئ حسابك — مجانًا خلال المعاينة العامة.'
                  : 'Sign in or create your account. Private preview, free during the preview.'}
              </p>
            )}
          </div>

          {/* Invitation-gate notice — set by AuthContext.checkAccess when
              a Supabase-authed user has no matching invite record.
              C-2 (Feb 06 2026): now renders the bilingual copy via
              `gateMessageFor`, which translates the structured
              `{ code, params }` payload AuthContext writes. */}
          {authError && (
            <div
              dir={isArabic ? 'rtl' : 'ltr'}
              style={{
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                fontSize: 13,
                color: '#EDEBE6',
                lineHeight: 1.6,
                marginBottom: 16,
                textAlign: 'center',
              }}
              className={isArabic ? 'font-arabic' : undefined}
              data-testid="auth-invitation-gate-message"
            >
              {gateMessageFor(authError)}
            </div>
          )}

          {/* Local error (bad credentials, rate-limit, etc.) */}
          {error && (
            <div
              className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-lg mb-4 flex items-center gap-2"
              data-testid="auth-error-message"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Google — PRIMARY action for the open-launch flow (Jul 2026):
              most joiners come via Google; email/password is secondary. */}
          {mode === 'login' && (
            <>
              <button
                onClick={handleSignInWithGoogle}
                disabled={googleLoading}
                className="w-full bg-[#EDEBE6] text-[#111111] p-3 rounded-full hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-colors font-medium"
                data-testid="google-auth-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  </span>
                )}
                <span>{t.google}</span>
              </button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-[#101010] px-3 text-xs uppercase tracking-wider text-[#7A766E]">{t.or}</span>
                </div>
              </div>
            </>
          )}

          {/* Form (email/password OR reset) */}
          <form onSubmit={mode === 'login' ? handleLogin : handleResetPassword}>
            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder={t.email}
                className="w-full p-3 border border-white/15 rounded-xl bg-white/5 text-[#EDEBE6] placeholder:text-[#77736B] focus:outline-none focus:ring-2 focus:ring-white/25 focus:border-white/40 transition-shadow"
                required
                dir="ltr"
                data-testid="auth-email-input"
              />
            </div>

            {mode === 'login' && (
              <div className="mb-4">
                <PasswordInput
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder={t.password}
                  showPassword={showPassword}
                  onToggle={() => setShowPassword(!showPassword)}
                  testId="auth-password-input"
                />
              </div>
            )}

            {mode === 'login' && (
              <div className="mb-4 text-left">
                <button
                  type="button"
                  onClick={() => switchMode('reset')}
                  className="text-sm text-[#A8A49C] hover:text-white transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t.forgotPassword}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={cn(
                'w-full p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium',
                mode === 'login'
                  ? 'border border-white/20 bg-transparent text-[#EDEBE6] hover:bg-white/10'
                  : 'bg-[#EDEBE6] text-[#111111] hover:bg-white',
              )}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t.loading}</span>
                </>
              ) : (
                <span>{mode === 'login' ? t.login : t.sendReset}</span>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-5 text-center text-sm">
            {mode === 'login' && (
              <p
                className={cn('text-[#A8A49C]', isArabic && 'font-arabic')}
                data-testid="auth-request-access-row"
              >
                {isArabic
                  ? 'تسجيل الدخول يُنشئ حسابك تلقائيًا.'
                  : 'Signing in creates your account automatically.'}
              </p>
            )}
            {mode === 'reset' && (
              <button
                onClick={() => switchMode('login')}
                className="text-[#A8A49C] hover:text-white"
                data-testid="back-to-login"
              >
                {t.backToLogin}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
